import * as i0 from '@angular/core';
import { Component, ChangeDetectionStrategy, Directive, Input, NgModule } from '@angular/core';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import { Workflow, EMPTY_ITEM, AdapterPropName, makeDatasource } from 'vscroll';
export { Routines, SizeStrategy } from 'vscroll';
import { Subject, BehaviorSubject } from 'rxjs';

var consumer = {
    name: 'ngx-ui-scroll',
    version: '3.2.0-beta.2'
};

class UiScrollComponent {
    constructor(changeDetector, elementRef, ngZone) {
        this.changeDetector = changeDetector;
        this.elementRef = elementRef;
        this.ngZone = ngZone;
        // the only template variable
        this.items = [];
    }
    ngOnInit() {
        // The workflow should be created outside of the Angular zone because it's causing many
        // change detection cycles. It runs its `init()` function in a `setTimeout` task, which
        // then sets up the `scroll` listener. The `scroll` event listener would force Angular to
        // run `tick()` any time the `scroll` task is invoked. We don't care about `scroll` events
        // since they're handled internally by `vscroll`. We still run change detection manually
        // tho when the `run` function is invoked.
        // `scroll` events may be also unpatched through zone.js flags:
        // `(window as any).__zone_symbol__UNPATCHED_EVENTS = ['scroll']`.
        // Having `runOutsideAngular` guarantees we're responsible for running change detection
        // only when it's "required" (when `items` are updated and the template should be updated too).
        this.workflow = this.ngZone.runOutsideAngular(() => new Workflow(Object.assign({ consumer, element: this.elementRef.nativeElement, datasource: this.datasource, run: (items) => {
                if (!items.length && !this.items.length) {
                    return;
                }
                // Re-enter the Angular zone only when items are set and when we have to run the local change detection.
                this.ngZone.run(() => {
                    this.items = items;
                    this.changeDetector.detectChanges();
                });
            } }, (this.Routines ? { Routines: this.Routines } : {}))));
    }
    ngOnDestroy() {
        var _a;
        (_a = this.workflow) === null || _a === void 0 ? void 0 : _a.dispose();
    }
}
UiScrollComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: UiScrollComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component });
UiScrollComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: UiScrollComponent, selector: "[ui-scroll]", ngImport: i0, template: ` <div data-padding-backward></div>
    <div
      *ngFor="let item of items"
      [attr.data-sid]="item.$index"
      [style.position]="item.invisible ? 'fixed' : null"
      [style.left]="item.invisible ? '-99999px' : null"
    >
      <ng-template
        [ngTemplateOutlet]="template"
        [ngTemplateOutletContext]="{
          $implicit: item.data,
          index: item.$index,
          odd: item.$index % 2,
          even: !(item.$index % 2)
        }"
      ></ng-template>
    </div>
    <div data-padding-forward></div>`, isInline: true, directives: [{ type: i1.NgForOf, selector: "[ngFor][ngForOf]" }, { type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: UiScrollComponent, decorators: [{
            type: Component,
            args: [{
                    selector: '[ui-scroll]',
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    template: ` <div data-padding-backward></div>
    <div
      *ngFor="let item of items"
      [attr.data-sid]="item.$index"
      [style.position]="item.invisible ? 'fixed' : null"
      [style.left]="item.invisible ? '-99999px' : null"
    >
      <ng-template
        [ngTemplateOutlet]="template"
        [ngTemplateOutletContext]="{
          $implicit: item.data,
          index: item.$index,
          odd: item.$index % 2,
          even: !(item.$index % 2)
        }"
      ></ng-template>
    </div>
    <div data-padding-forward></div>`
                }]
        }], ctorParameters: function () { return [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: i0.NgZone }]; } });

class UiScrollDirective {
    constructor(templateRef, viewContainer) {
        this.templateRef = templateRef;
        this.viewContainer = viewContainer;
    }
    set uiScrollOf(datasource) {
        this.datasource = datasource;
    }
    set uiScrollRoutines(Routines) {
        this.Routines = Routines;
    }
    ngOnInit() {
        const componentRef = this.viewContainer.createComponent(UiScrollComponent);
        componentRef.instance.datasource = this.datasource;
        componentRef.instance.template = this.templateRef;
        componentRef.instance.Routines = this.Routines;
    }
}
UiScrollDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: UiScrollDirective, deps: [{ token: i0.TemplateRef }, { token: i0.ViewContainerRef }], target: i0.ɵɵFactoryTarget.Directive });
UiScrollDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.17", type: UiScrollDirective, selector: "[uiScroll][uiScrollOf]", inputs: { uiScrollOf: "uiScrollOf", uiScrollRoutines: "uiScrollRoutines" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: UiScrollDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[uiScroll][uiScrollOf]' }]
        }], ctorParameters: function () { return [{ type: i0.TemplateRef }, { type: i0.ViewContainerRef }]; }, propDecorators: { uiScrollOf: [{
                type: Input
            }], uiScrollRoutines: [{
                type: Input
            }] } });

class UiScrollModule {
}
UiScrollModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: UiScrollModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
UiScrollModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: UiScrollModule, declarations: [UiScrollComponent, UiScrollDirective], imports: [CommonModule], exports: [UiScrollDirective] });
UiScrollModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: UiScrollModule, imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: UiScrollModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [UiScrollComponent, UiScrollDirective],
                    imports: [CommonModule],
                    exports: [UiScrollDirective]
                }]
        }] });

const getBooleanSubjectPropConfig = () => ({
    source: new Subject(),
    emit: (source, value) => source.next(value)
});
const getItemBehaviorSubjectPropConfig = () => ({
    source: new BehaviorSubject(EMPTY_ITEM),
    emit: (source, value) => source.next(value)
});
const getAdapterConfig = () => ({
    mock: false,
    reactive: {
        [AdapterPropName.init$]: getBooleanSubjectPropConfig(),
        [AdapterPropName.isLoading$]: getBooleanSubjectPropConfig(),
        [AdapterPropName.loopPending$]: getBooleanSubjectPropConfig(),
        [AdapterPropName.firstVisible$]: getItemBehaviorSubjectPropConfig(),
        [AdapterPropName.lastVisible$]: getItemBehaviorSubjectPropConfig(),
        [AdapterPropName.bof$]: getBooleanSubjectPropConfig(),
        [AdapterPropName.eof$]: getBooleanSubjectPropConfig()
    }
});
const AngularDatasource = makeDatasource(getAdapterConfig);

/**
 * Generated bundle index. Do not edit.
 */

export { AngularDatasource as Datasource, UiScrollDirective, UiScrollModule };
